import { useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  PaginationItem,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TableStatusRow from "../TableStatusRow/TableStatusRow";
import { DEFAULT_TIME_ZONE } from "../../utils/timeZones";
import "./ActivityLogsTable.scss";

dayjs.extend(utc);
dayjs.extend(timezone);

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100, 200, 500];

// Text labels for first/prev/next/last pagination buttons
const NAV_LABELS = { first: "First", previous: "Previous", next: "Next", last: "Last" };

const renderActivityBadge = (type) => {
  if (!type) return "-";
  const t = String(type).toLowerCase();
  let toneStyle = { bg: "#f1f5f9", color: "#475569", border: "#e2e8f0", dot: "#94a3b8" };

  if (t.includes("survey")) {
    toneStyle = { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe", dot: "#3b82f6" };
  } else if (t.includes("socket")) {
    toneStyle = { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0", dot: "#10b981" };
  } else if (t.includes("bluetooth")) {
    toneStyle = { bg: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe", dot: "#8b5cf6" };
  } else if (t.includes("upload") || t.includes("sync")) {
    toneStyle = { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", dot: "#22c55e" };
  } else if (t.includes("online")) {
    toneStyle = { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", dot: "#22c55e" };
  } else if (t.includes("offline")) {
    toneStyle = { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca", dot: "#ef4444" };
  } else if (t.includes("logout") || t.includes("log out")) {
    toneStyle = { bg: "#f8fafc", color: "#64748b", border: "#cbd5e1", dot: "#94a3b8" };
  } else if (t.includes("login") || t.includes("log in")) {
    toneStyle = { bg: "#fffbeb", color: "#b45309", border: "#fde68a", dot: "#f59e0b" };
  } else if (t.includes("mode")) {
    toneStyle = { bg: "#f0fdf4", color: "#047857", border: "#bbf7d0", dot: "#10b981" };
  }

  return (
    <span
      className="activity_badge"
      style={{
        backgroundColor: toneStyle.bg,
        color: toneStyle.color,
        border: `1px solid ${toneStyle.border}`,
      }}
    >
      <span className="activity_badge_dot" style={{ backgroundColor: toneStyle.dot }} />
      {type}
    </span>
  );
};

const FormatAdditionalInfo = ({ info }) => {
  const [expanded, setExpanded] = useState(false);
  if (!info) return "-";

  const payloadIndex = info.indexOf("Payload=");
  if (payloadIndex !== -1) {
    const summaryText = info.slice(0, payloadIndex).trim();
    const rawPayload = info.slice(payloadIndex + 8).trim();

    return (
      <div className="activity_info_payload_container">
        <span className="activity_info_summary">{summaryText}</span>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="activity_info_payload_btn"
        >
          {expanded ? "Hide Details" : "View Payload"}
        </button>
        {expanded && (
          <pre className="activity_payload_pre">
            {tryFormatJson(rawPayload)}
          </pre>
        )}
      </div>
    );
  }

  if (info.length > 120) {
    return (
      <div className="activity_info_text_wrap">
        <span>{expanded ? info : `${info.slice(0, 120)}...`}</span>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="activity_info_toggle_btn"
        >
          {expanded ? "Show Less" : "Show More"}
        </button>
      </div>
    );
  }

  return <span>{info}</span>;
};

const tryFormatJson = (str) => {
  try {
    let cleanStr = str;
    if (cleanStr.startsWith("[") && cleanStr.endsWith("]")) {
      cleanStr = cleanStr.slice(1, -1);
    }
    const parsed = JSON.parse(cleanStr);
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    return str;
  }
};

// Reusable Pagination Bar containing summary on left, pages in center, and rows per page in the far right corner
const PaginationBar = ({
  position = "top",
  totalRows,
  rowsPerPage,
  page,
  onPageChange,
  onRowsPerPageChange,
  isLoading,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const fromRecord = totalRows === 0 ? 0 : page * rowsPerPage + 1;
  const toRecord = Math.min((page + 1) * rowsPerPage, totalRows);

  return (
    <div className={`logs_pagination_bar logs_pagination_bar--${position}`}>
      <div className="logs_pagination_left">
        {totalRows > 0 ? (
          <span className="logs_pagination_summary">
            Showing <strong>{fromRecord.toLocaleString()}–{toRecord.toLocaleString()}</strong> of{" "}
            <strong>{totalRows.toLocaleString()}</strong> entries
          </span>
        ) : isLoading ? (
          <span className="logs_pagination_summary logs_pagination_summary--loading">
            Loading logs...
          </span>
        ) : (
          <span className="logs_pagination_summary">0 logs found</span>
        )}
      </div>

      <div className="logs_pagination_center">
        <Pagination
          className="logs_pagination_pages"
          count={totalPages}
          page={page + 1}
          onChange={(event, newPage) => onPageChange(event, newPage - 1)}
          showFirstButton
          showLastButton
          shape="rounded"
          renderItem={(item) => (
            <PaginationItem
              {...item}
              components={
                NAV_LABELS[item.type]
                  ? { [item.type]: () => NAV_LABELS[item.type] }
                  : undefined
              }
            />
          )}
        />
      </div>

      <div className="logs_pagination_right">
        <div className="logs_pagesize_wrapper">
          <label htmlFor={`logs-page-size-${position}`} className="logs_pagesize_label">
            Rows per page:
          </label>
          <Select
            id={`logs-page-size-${position}`}
            className="logs_select_size"
            size="small"
            value={rowsPerPage}
            onChange={onRowsPerPageChange}
          >
            {ROWS_PER_PAGE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
    </div>
  );
};

const ActivityLogsTable = ({
  activities = [],
  isLoading,
  fetchError,
  onRetry,
  totalRows = 0,
  rowsPerPage = 100,
  page = 0,
  onPageChange,
  onRowsPerPageChange,
  timeZone = DEFAULT_TIME_ZONE,
}) => {
  const scrollToTop = () => {
    const mainContent = document.querySelector(".mainlayout_content");
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: "smooth" });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToBottom = () => {
    const mainContent = document.querySelector(".mainlayout_content");
    if (mainContent) {
      mainContent.scrollTo({ top: mainContent.scrollHeight, behavior: "smooth" });
    }
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
  };

  const isLargeTable = activities && activities.length >= 10;

  return (
    <div className="logs_table_card">
      {/* Top Header Pagination & Summary Bar */}
      <PaginationBar
        position="top"
        totalRows={totalRows}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        isLoading={isLoading}
      />

      {/* Main Table */}
      <TableContainer component={Paper} className="activity_table_paper" elevation={0}>
        <Table>
          <TableHead className="activity_table_head">
            <TableRow>
              <TableCell className="activity_th" style={{ width: "15%" }}>
                Device ID
              </TableCell>
              <TableCell className="activity_th" style={{ width: "20%" }}>
                Date ({timeZone})
              </TableCell>
              <TableCell className="activity_th" style={{ width: "16%" }}>
                Activity Type
              </TableCell>
              <TableCell className="activity_th" style={{ width: "49%" }}>
                Additional Info
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activities.length > 0 ? (
              activities.map((log, index) => (
                <TableRow key={log.id || index} className="activity_table_row">
                  <TableCell className="activity_td activity_td--device">
                    <span className="activity_device_code">{log.deviceId || "-"}</span>
                  </TableCell>
                  <TableCell className="activity_td activity_td--date">
                    {log.activityLoggedDate
                      ? dayjs
                          .utc(log.activityLoggedDate)
                          .tz(timeZone)
                          .format("DD MMM YYYY, hh:mm:ss A")
                      : "-"}
                  </TableCell>
                  <TableCell className="activity_td">
                    {renderActivityBadge(log.activityType)}
                  </TableCell>
                  <TableCell className="activity_td activity_td--info">
                    <FormatAdditionalInfo info={log.additionalInfo} />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableStatusRow
                colSpan={4}
                loading={isLoading}
                error={fetchError}
                onRetry={onRetry}
                emptyMessage="No activity found"
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bottom Footer Pagination & Summary Bar */}
      <PaginationBar
        position="bottom"
        totalRows={totalRows}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        isLoading={isLoading}
      />

      {/* Floating Scroll to Top / Bottom Controls (only for large log tables) */}
      {isLargeTable && (
        <div className="logs_floating_scroll_controls" aria-label="Page scroll controls">
          <Tooltip title="Scroll to top" placement="left" arrow>
            <button
              type="button"
              className="logs_floating_scroll_btn logs_floating_scroll_btn--up"
              onClick={scrollToTop}
              aria-label="Scroll to top"
            >
              <KeyboardArrowUpIcon />
            </button>
          </Tooltip>
          <div className="logs_floating_scroll_divider" />
          <Tooltip title="Scroll to bottom" placement="left" arrow>
            <button
              type="button"
              className="logs_floating_scroll_btn logs_floating_scroll_btn--down"
              onClick={scrollToBottom}
              aria-label="Scroll to bottom"
            >
              <KeyboardArrowDownIcon />
            </button>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default ActivityLogsTable;

